import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../services/apiClient';
import { ArrowPathIcon } from '../../components/Icons';
import { useAuth } from '../../contexts/AuthContext';
import { DataListTable, type Column } from '../../components/crm/DataListTable';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
  is_superuser?: boolean;
  tenant?: { id: string; name: string } | null;
  created_at: string | null;
}

interface TenantRow {
  id: string;
  name: string;
}

const ROLES = ['admin', 'sales', 'employee', 'user'] as const;

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', role: '', phone: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const [resetLoading, setResetLoading] = useState<string | null>(null);

  const fetchTenants = async () => {
    try {
      const data = await apiClient.get('/platform/admin/tenants/');
      setTenants(Array.isArray(data) ? data : []);
    } catch {
      setTenants([]);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const params: Record<string, string> = {};
      if (tenantFilter) params.tenant = tenantFilter;
      if (emailFilter) params.email = emailFilter;
      if (roleFilter) params.role = roleFilter;
      if (activeFilter) params.is_active = activeFilter;
      const data = await apiClient.get('/platform/admin/users/', params);
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [tenantFilter, emailFilter, roleFilter, activeFilter]);

  const handleEditOpen = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      role: user.role,
      phone: (user as { phone?: string }).phone || '',
      is_active: user.is_active,
    });
  };

  const handleForcePasswordReset = async (userId: string) => {
    setResetLoading(userId);
    try {
      await apiClient.post(`/platform/admin/users/${userId}/force-password-reset/`, {});
      setError('');
      await fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send password reset');
    } finally {
      setResetLoading(null);
    }
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await apiClient.patch(`/platform/admin/users/${selectedUser.id}/`, {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        role: editForm.role,
        phone: editForm.phone,
        is_active: editForm.is_active,
      });
      setSelectedUser(null);
      await fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns = useMemo<Column<User>[]>(
    () => [
      {
        id: 'user',
        header: 'User',
        mobilePriority: 'primary',
        mobileLabel: 'User',
        render: (user) => (
          <div>
            <div className="text-sm font-medium text-white">
              {user.first_name} {user.last_name}
            </div>
            <div className="text-sm text-fg-muted">{user.email}</div>
            {user.is_superuser && (
              <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded">
                Superuser
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'tenant',
        header: 'Tenant',
        mobileLabel: 'Tenant',
        render: (user) => <span className="text-sm text-fg-muted">{user.tenant?.name ?? '-'}</span>,
      },
      {
        id: 'role',
        header: 'Role',
        mobileLabel: 'Role',
        render: (user) => (
          <span className="px-2 py-1 text-xs rounded bg-surface-muted text-fg-secondary">{user.role}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        mobileLabel: 'Status',
        render: (user) => (
          <span
            className={`px-2 py-1 text-xs rounded ${
              user.is_active ? 'bg-emerald-900/30 text-success-fg' : 'bg-surface-muted text-fg-muted'
            }`}
          >
            {user.is_active ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        id: 'joined',
        header: 'Joined',
        mobileLabel: 'Joined',
        render: (user) => <span className="text-sm text-fg-muted">{formatDate(user.created_at)}</span>,
      },
      {
        id: 'actions',
        header: 'Actions',
        mobileLabel: 'Actions',
        render: (user) => (
          <div data-row-action className="flex gap-3">
            <button
              onClick={() => handleEditOpen(user)}
              className="text-amber-400 hover:text-amber-300 text-sm"
            >
              Edit
            </button>
            <button
              onClick={() => {
                if (window.confirm(`Send password reset email to ${user.email}?`)) {
                  handleForcePasswordReset(user.id);
                }
              }}
              disabled={resetLoading !== null}
              className="text-fg-muted hover:text-white text-sm disabled:opacity-50"
            >
              {resetLoading === user.id ? 'Sending...' : 'Password reset'}
            </button>
          </div>
        ),
      },
    ],
    [resetLoading]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Platform Users</h1>
        <button
          onClick={() => fetchUsers()}
          disabled={loading}
          className="px-4 py-2 rounded-lg border border-border hover:border-amber-500/50 hover:bg-surface-muted/50 disabled:opacity-50 text-fg-secondary text-sm flex items-center gap-2"
        >
          <ArrowPathIcon size={4} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <div className="bg-app border border-border rounded-lg p-4">
        <h2 className="text-sm font-medium text-fg-muted mb-3">Filters</h2>
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-xs text-fg-muted mb-1">Tenant</label>
            <select
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
              className="px-3 py-2 bg-surface-muted border border-border rounded-lg text-white text-sm"
            >
              <option value="">All Tenants</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-fg-muted mb-1">Email</label>
            <input
              type="text"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              placeholder="Search email..."
              className="px-3 py-2 bg-surface-muted border border-border rounded-lg text-white text-sm placeholder-slate-500 w-48"
            />
          </div>
          <div>
            <label className="block text-xs text-fg-muted mb-1">Role</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-2 bg-surface-muted border border-border rounded-lg text-white text-sm"
            >
              <option value="">All Roles</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-fg-muted mb-1">Status</label>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="px-3 py-2 bg-surface-muted border border-border rounded-lg text-white text-sm"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <DataListTable<User>
        columns={columns}
        rows={users}
        getRowKey={(user) => user.id}
        loading={loading}
        emptyContent={
          <div className="p-8 text-center text-fg-muted">No users found.</div>
        }
      />

      {selectedUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-app border border-border rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">Edit User</h3>
              <button
                onClick={() => setSelectedUser(null)}
                className="text-fg-muted hover:text-white"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-fg-muted text-sm">{selectedUser.email}</p>
              <div>
                <label className="block text-xs text-fg-muted mb-1">First Name</label>
                <input
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-fg-muted mb-1">Last Name</label>
                <input
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-fg-muted mb-1">Phone</label>
                <input
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-fg-muted mb-1">Role</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-white"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={editForm.is_active}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                  disabled={currentUser?.id === selectedUser.id}
                  className="rounded border-border"
                />
                <label htmlFor="is_active" className="text-sm text-fg-muted">Active</label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 font-medium"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setSelectedUser(null)}
                className="px-4 py-2 border border-border text-fg-muted rounded-lg hover:bg-surface-muted"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
